/**
 * Pure, React-free core of on-demand FK label resolution: key-set diffing,
 * chunking, the merge/failure contract, and the per-target failure cooldown.
 *
 * This module owns every DECISION; the React hook that will orchestrate the
 * actual `fetchTableExplorerRows` calls (a later slice) stays a thin loop
 * over what this module returns. Nothing here talks to Electron, React, or
 * TanStack Query.
 *
 * One `LookupLabelAccumulator` lives per on-demand target — the tuple
 * `(connection, database, targetSchema, targetTable, targetKeyColumn,
 * labelColumn)` — shared across BOTH grids and every column that points at
 * the same target, so a key resolved for one column/grid is never re-fetched
 * by another. Callers key their registry with `accumulatorKey`.
 *
 * Absent-vs-null contract, reused verbatim from `buildLookupLabelMap`
 * (`./lookupLabelMap.ts`):
 *  - `labels.has(key) === false` — unknown: never fetched, or a fetch is
 *    still in flight. Fetchable.
 *  - `labels.get(key) === null` (present) — resolved: the target row either
 *    doesn't exist for this key, or exists with a NULL label column. Either
 *    way, never refetched — that is what stops a refetch loop on orphaned
 *    keys (a key from a stale FK, a deleted target row, ...).
 *  - `labels.get(key) === <string>` — resolved with a real label.
 *
 * A batch FAILURE (network error, dropped connection, revoked grant) is
 * different from a batch that came back empty: it must NOT resolve the
 * requested keys to `null`, or a transient failure would permanently hide
 * labels that really exist. Instead it leaves them unknown and opens a
 * cooldown window on the whole target (`markBatchFailed`), reusing
 * `LOOKUP_REFRESH_THROTTLE_MS` from `./lookupCacheRefresh.ts` so this and
 * the cached-lookup self-heal path share one notion of "how long before we
 * try an unhappy target again."
 */

import { LOOKUP_REFRESH_THROTTLE_MS } from './lookupCacheRefresh';

/** Oracle's `IN (...)` list caps at 1000 elements; 500 leaves headroom for
 *  any other predicate the caller composes alongside it. */
export const MAX_LOOKUP_BATCH_KEYS = 500;

/** Accumulator for one on-demand target: (connection, database,
 *  targetSchema, targetTable, labelColumn). Shared by every column across
 *  both grids that resolves labels from this same target. */
export type LookupLabelAccumulator = {
  /** Resolved key -> label. Absent = unknown/fetchable; see the module doc
   *  comment for the full absent-vs-null contract. */
  labels: Map<string, string | null>;
  /** Keys currently being fetched — excluded from the next unknown-set so a
   *  second settled window can't re-request a batch already in flight. */
  inFlight: Set<string>;
  /** No fetches for this target until this timestamp (ms, `Date.now()`
   *  scale). `0` — the zero value of `createAccumulator` — never blocks. */
  cooldownUntil: number;
};

/** A fresh accumulator: nothing resolved, nothing in flight, no cooldown. */
export function createAccumulator(): LookupLabelAccumulator {
  return { labels: new Map(), inFlight: new Set(), cooldownUntil: 0 };
}

/**
 * How many on-demand targets the registry keeps at once. Each accumulator
 * holds up to a window's worth of resolved keys and lives for the session, so
 * an unbounded registry grows with every distinct target the user visits —
 * every reference table, in every database, across a long session. 32 is well
 * past what any one screen resolves from (a grid resolves a handful of columns
 * at a time) while keeping the whole thing to a bounded, small footprint.
 */
export const MAX_LOOKUP_TARGETS = 32;

/**
 * Get (or create) the accumulator for one target, keeping the registry bounded
 * and least-recently-USED first: the returned entry is re-inserted so it moves
 * to the end of the Map's insertion order, and eviction takes from the front.
 *
 * Evicting only ever costs a re-fetch — the accumulator is a cache, not state
 * anyone reads a decision from — and the evicted target is by construction the
 * one nothing has asked about in the longest time.
 */
export function acquireAccumulator(
  registry: Map<string, LookupLabelAccumulator>,
  key: string,
  cap: number = MAX_LOOKUP_TARGETS,
): LookupLabelAccumulator {
  const existing = registry.get(key);
  if (existing) {
    registry.delete(key);
    registry.set(key, existing);
    return existing;
  }
  const created = createAccumulator();
  registry.set(key, created);
  while (registry.size > cap) {
    const oldest = registry.keys().next();
    if (oldest.done) break;
    registry.delete(oldest.value);
  }
  return created;
}

/**
 * Distinct, stringified, non-null keys from the visible rows for one column.
 * Stringified with `String(value)` — the same convention
 * `useLookupLabelSelfHeal` and `buildLookupLabelMap` use — so a numeric key
 * and its string form are always the same accumulator key.
 */
export function collectWindowKeys(
  rows: readonly Record<string, unknown>[],
  columnKey: string,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const value = row?.[columnKey];
    if (value == null) continue;
    seen.add(String(value));
  }
  return Array.from(seen);
}

/**
 * Keys needing a fetch, chunked into batches of at most
 * `MAX_LOOKUP_BATCH_KEYS`: in the visible window, not already resolved, not
 * already in flight. Returns `[]` — no chunks at all — while the target is
 * under its failure cooldown (`now < acc.cooldownUntil`); the caller isn't
 * meant to distinguish "nothing to fetch" from "not fetching yet", both mean
 * "don't issue a batch right now."
 */
export function planBatches(
  acc: LookupLabelAccumulator,
  windowKeys: readonly string[],
  now: number,
): string[][] {
  if (now < acc.cooldownUntil) return [];

  const unknown: string[] = [];
  for (const key of windowKeys) {
    if (acc.labels.has(key)) continue;
    if (acc.inFlight.has(key)) continue;
    unknown.push(key);
  }
  if (unknown.length === 0) return [];

  const batches: string[][] = [];
  for (let i = 0; i < unknown.length; i += MAX_LOOKUP_BATCH_KEYS) {
    batches.push(unknown.slice(i, i + MAX_LOOKUP_BATCH_KEYS));
  }
  return batches;
}

/** Mark keys as in flight — call right before issuing the fetch so a
 *  concurrently-settled window can't plan the same batch twice. */
export function markInFlight(acc: LookupLabelAccumulator, keys: readonly string[]): void {
  for (const key of keys) acc.inFlight.add(key);
}

/**
 * Merge one successful batch result. Every REQUESTED key becomes resolved —
 * this is what stops a refetch loop on a key the target never returns (an
 * orphaned FK value, a deleted target row, ...):
 *  - a key present in `rows` resolves to its `label` (an explicit `null`
 *    label is PRESERVED — that's a real, known-NULL label column value);
 *  - a `label` of `undefined`, or a row missing the field entirely, is
 *    treated the same as an explicit `null`;
 *  - a requested key with NO row in the result resolves to `null` —
 *    known-missing, never refetched.
 *
 * Row keys are stringified with the same `String(value)` convention as
 * `collectWindowKeys`, so a numeric `key` column matches a string-keyed
 * request. A row whose key was never requested is ignored — only requested
 * keys are ever written into the accumulator.
 *
 * Clears in-flight for every requested key, found or not.
 */
export function mergeBatchResult(
  acc: LookupLabelAccumulator,
  requestedKeys: readonly string[],
  rows: readonly { key: unknown; label: unknown }[],
): void {
  const found = new Map<string, string | null>();
  for (const row of rows) {
    const key = String(row.key);
    const label = row.label === undefined ? null : (row.label as string | null);
    found.set(key, label);
  }

  for (const key of requestedKeys) {
    acc.labels.set(key, found.has(key) ? found.get(key)! : null);
    acc.inFlight.delete(key);
  }
}

/**
 * Merge one failed batch. Unlike `mergeBatchResult`, the requested keys stay
 * UNKNOWN (not resolved to `null`) — a transient failure must never masquerade
 * as "this target has no such row." In-flight is cleared for the requested
 * keys so a later settled window can retry them, but not before the cooldown
 * (`now + LOOKUP_REFRESH_THROTTLE_MS`) elapses for the whole target.
 */
export function markBatchFailed(
  acc: LookupLabelAccumulator,
  requestedKeys: readonly string[],
  now: number,
): void {
  for (const key of requestedKeys) acc.inFlight.delete(key);
  acc.cooldownUntil = now + LOOKUP_REFRESH_THROTTLE_MS;
}

/**
 * The per-target accumulator registry key: `(connectionId, database,
 * toSchema, toTable, toColumn, labelColumn)` joined with `|`, mirroring
 * `lookupRefreshKey`'s style so both throttling schemes read the same way.
 * `toSchema` may legitimately be `''` (the Kusto no-schema convention) — the
 * pipe separator can't collide with a schema/table/column name, so an empty
 * component is still a distinct, valid key.
 *
 * `toColumn` is part of the identity because the accumulator maps KEYS to
 * labels, and a key only means a row relative to the column it was matched
 * on: `Orders.customerId → Customers.id` and `Orders.customerCode →
 * Customers.code` read the same table with the same label column, but a key of
 * `"1"` is a different customer in each. Omitting it let the second column
 * adopt the first one's labels — and, because every key was already
 * "resolved", never fetch anything of its own to correct them.
 */
export function accumulatorKey(
  connectionId: string,
  database: string,
  toSchema: string,
  toTable: string,
  toColumn: string,
  labelColumn: string,
  declarationId = '',
  targetFilter = '',
): string {
  const key = `${connectionId}|${database}|${toSchema}|${toTable}|${toColumn}|${labelColumn}`;
  // Declaration id + canonical target filter (Azure Table two-key lookups) only
  // extend the key when present, so every SQL key stays exactly what it was.
  return declarationId || targetFilter ? `${key}|${declarationId}|${targetFilter}` : key;
}
